
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

// Interface definitions
interface PortfolioItem {
  id: string;
  title: string;
  subtitle: string;
  tags: string[];
  image: string;
  link: string;
  description: string;
  features: string[];
  technologies: string[];
  results: string[];
}

interface PortfolioModalProps {
  item: PortfolioItem | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ImageModalProps {
  imageUrl: string;
  imageAlt: string;
  isOpen: boolean;
  onClose: () => void;
}

interface ImageEditorProps {
  imageUrl: string;
  imageAlt: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (editedImageUrl: string) => void;
}

// ImageEditor component with i18n
function ImageEditor({ imageUrl, imageAlt, isOpen, onClose, onSave }: ImageEditorProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);

  // Existing logic
  useEffect(() => {
    if (isOpen && imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setOriginalImage(img);
        drawImage(img);
      };
      img.src = imageUrl;
    }
  }, [isOpen, imageUrl]);

  useEffect(() => {
    if (originalImage) {
      drawImage(originalImage);
    }
  }, [brightness, contrast, saturation, rotation, scale, originalImage]);

  const drawImage = (img: HTMLImageElement) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 800;
    canvas.height = 600;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    // 中央に移動
    ctx.translate(canvas.width / 2, canvas.height / 2);
    
    // 回転とスケール適用
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(scale, scale);

    // フィルター適用
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}% )`;

    // 画像を中央に描画
    const aspectRatio = img.width / img.height;
    let drawWidth = canvas.width * 0.8;
    let drawHeight = drawWidth / aspectRatio;

    if (drawHeight > canvas.height * 0.8) {
      drawHeight = canvas.height * 0.8;
      drawWidth = drawHeight * aspectRatio;
    }

    ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
    ctx.restore();
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const editedImageUrl = canvas.toDataURL('image/png');
    onSave(editedImageUrl);
    onClose();
  };

  const handleReset = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setRotation(0);
    setScale(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-stone-800">{t('portfolio.modal.imageEditor')}</h3>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-stone-100 hover:bg-stone-200 rounded-full flex items-center justify-center text-stone-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* キャンバス */}
            <div className="lg:col-span-2">
              <div className="bg-stone-100 rounded-xl p-4 flex items-center justify-center min-h-[400px]">
                <canvas
                  ref={canvasRef}
                  className="max-w-full max-h-full border border-stone-200 rounded-lg shadow-lg"
                />
              </div>
            </div>

            {/* コントロールパネル */}
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-stone-800 mb-4">{t('portfolio.modal.adjustments')}</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {t('portfolio.modal.brightness')}: {brightness}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                      className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {t('portfolio.modal.contrast')}: {contrast}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={contrast}
                      onChange={(e) => setContrast(Number(e.target.value))}
                      className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {t('portfolio.modal.saturation')}: {saturation}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={saturation}
                      onChange={(e) => setSaturation(Number(e.target.value))}
                      className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {t('portfolio.modal.rotation')}: {rotation}°
                    </label>
                    <input
                      type="range"
                      min="-180"
                      max="180"
                      value={rotation}
                      onChange={(e) => setRotation(Number(e.target.value))}
                      className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {t('portfolio.modal.scale')}: {scale.toFixed(1)}x
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={scale}
                      onChange={(e) => setScale(Number(e.target.value))}
                      className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-stone-800 mb-4">{t('portfolio.modal.quickAdjust')}</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setRotation(rotation - 90)}
                    className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-rotate-lock-line mr-1"></i>
                    {t('portfolio.modal.rotateLeft')}
                  </button>
                  <button
                    onClick={() => setRotation(rotation + 90)}
                    className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-rotate-lock-fill mr-1"></i>
                    {t('portfolio.modal.rotateRight')}
                  </button>
                  <button
                    onClick={() => setScale(Math.min(2, scale * 1.2))}
                    className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-zoom-in-line mr-1"></i>
                    {t('portfolio.modal.zoomIn')}
                  </button>
                  <button
                    onClick={() => setScale(Math.max(0.5, scale * 0.8))}
                    className="px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-zoom-out-line mr-1"></i>
                    {t('portfolio.modal.zoomOut')}
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-stone-200">
                <div className="flex gap-3">
                  <button
                    onClick={handleReset}
                    className="flex-1 px-4 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    {t('portfolio.modal.reset')}
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-save-line mr-2"></i>
                    {t('portfolio.modal.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Existing ImageModal component
function ImageModal({ imageUrl, imageAlt, isOpen, onClose }: ImageModalProps) {
  const [showEditor, setShowEditor] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);

  useEffect(() => {
    setCurrentImageUrl(imageUrl);
  }, [imageUrl]);

  const handleEditClick = () => {
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
  };

  const handleSaveEdit = (editedImageUrl: string) => {
    setCurrentImageUrl(editedImageUrl);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
        <div className="relative max-w-5xl max-h-[90vh] w-full h-full flex items-center justify-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          
          <button
            onClick={handleEditClick}
            className="absolute top-4 left-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors z-10 cursor-pointer"
          >
            <i className="ri-edit-line text-2xl"></i>
          </button>
          
          <img
            src={currentImageUrl}
            alt={imageAlt}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>

      <ImageEditor
        imageUrl={currentImageUrl}
        imageAlt={imageAlt}
        isOpen={showEditor}
        onClose={handleEditorClose}
        onSave={handleSaveEdit}
      />
    </>
  );
}

// PortfolioModal component with i18n and existing logic
function PortfolioModal({ item, isOpen, onClose }: PortfolioModalProps) {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<{url: string, alt: string} | null>(null);

  // Existing code
  const handleImageClick = (imageUrl: string, imageAlt: string) => {
    setSelectedImage({ url: imageUrl, alt: imageAlt });
  };

  const handleImageModalClose = () => {
    setSelectedImage(null);
  };

  if (!isOpen || !item) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-stone-600 hover:text-stone-800 transition-colors z-10 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-64 object-cover cursor-pointer"
              onClick={() => handleImageClick(item.image, item.title)}
            />
            
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center text-lg font-bold text-stone-600">
              {item.id}
            </div>
          </div>
          
          <div className="p-8">
            <h2 className="text-3xl font-bold text-stone-800 mb-4">{item.title}</h2>
            <p className="text-xl text-stone-600 mb-6">{item.subtitle}</p>
            
            <div className="flex flex-wrap gap-2 mb-8">
              {item.tags.map((tag, index) => (
                <span key={index} className="px-4 py-2 bg-amber-100 text-amber-700 text-sm rounded-full font-medium">
                  {tag}
                </span>
              ))}
            </div>

            {/* 画像ギャラリー（全プロジェクト共通フォーマット） */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-stone-800 mb-4">{t('portfolio.modal.projectImages')}</h4>
              {/* Existing gallery code */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {item.id === '01' ? (
                  <>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/f832e489e393ebc4518290f1699e02e1.png"
                        alt="センサー内蔵スマートファブリック"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/cca67277032ef0a03dd9cc55aeea1887.png", "センサー内蔵スマートファブリック")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/136bf31b2ebd15a46fc031287f4dff61.png"
                        alt="光透過性ニット素材"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/136bf31b2ebd15a46fc031287f4dff61.png", "光透過性ニット素材")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/415d65ab35e6adc95806b404c7c4dcc2.png"
                        alt="プロジェクションマッピング対応パネル"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/03e6ee859c8067613f2660eccbaccee8.png", "プロジェクションマッピング対応パネル")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/3197fd239f0c6741993558e89824ae79.png"
                        alt="インダストリアル・ニッティング技術"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/0c005a5ca5ebc16d551bcf2abc6d51ae.png", "インダストリアル・ニッティング技術")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/435dae1c1be495dd4400473470497e83.png"
                        alt="インタラクティブ空間体験"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/9762c72aff3ad26a34f74b5986c361e4.png", "インタラクティブ空間体験")}
                      />
                    </div>
                  </>
                ) : item.id === '02' ? (
                  <>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/1f342d1584b58ed2e6ddde94c5326959.png"
                        alt="ニットスキン電子機器"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/dc24b8a88432f3b635ed1867fd9b964e.png", "ニットスキン電子機器")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/2367c64a622f7a8b36fc9edea10ec877.png"
                        alt="サステナブルプロダクト"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/54e129928f84b9ab0f17a4aabfa7e85e.png", "サステナブルプロダクト")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/83e340ae0e3f78f6c755256c4d310496.png"
                        alt="カスタマイズ可能デザイン"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/1592bb8944d467fac1657bf56272100b.png", "カスタマイズ可能デザイン")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/e24870e67474101e9c66a79415ce6ef8.png"
                        alt="3Dニッティング製造"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/d77ddcd81ffdfaf65aa88b45c98a49b8.png", "3Dニッティング製造")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/b9898178cf656d34090f5bcca26aa7e4.png"
                        alt="ユーザー体験"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/42eea90c9fde4293f59f839a378bbf93.png", "ユーザー体験")}
                      />
                    </div>
                  </>
                ) : item.id === '03' ? (
                  <>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/5b0d827811a293f0c384a831aa9452d6.png"
                        alt="ニットカバー植木鉢"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/b8882d75728fe3f182ba1a6f31ae0dfc.png", "ニットカバー植木鉢")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/79ccac04a3daac89e607e9e6e54e5a60.png"
                        alt="ニットシェード照明"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/f939c3f063d23562a191a0d1688e0b3f.png", "ニットシェード照明")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/851e24cd7a2bd973ad6e0f731f9f9fce.png"
                        alt="ニットカバーソファ"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/d026fccba3784558ee4733df2b80afb5.png", "ニットカバーソファ")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/adb7e29fda7f168a6cc43374d7c0a366.png"
                        alt="季節対応インテリア変化"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/d723dd095d13bd195190fbfa69ef4298.png", "季節対応インテリア変化")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/712525ea875dd05130802dce1a32efc8.png"
                        alt="インテリア体験"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/d7b112c6705e976b0629f471022c3a49.png", "インテリア体験")}
                      />
                    </div>
                  </>
                ) : item.id === '04' ? (
                  <>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/627a79ccb088b194c6b1959de5dfc0fc.png"
                        alt="家庭用ニットスキンロボット"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/ddaf19cd35d81248eb808f0f7a80d2e4.png", "家庭用ニットスキンロボット")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/a99044ae0f12b9bd4072b545df3e7f36.png"
                        alt="産業用ニットスキンロボット"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/852849aeffd1beff9b2ac060aadee3b5.png", "産業用ニットスキンロボット")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/3d756bc103f4010c3002defd01e1d0f2.png"
                        alt="医療用ニットスキンロボット"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/8089025bc3e27b87d06bcfa972d3538f.png", "医療用ニットスキンロボット")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/41d4c7aa96dc9de269193d1931991efc.png"
                        alt="ロボットスキンカスタマイズ"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/b9b3863a9dbf7408401078aab7c852a3.png", "ロボットスキンカスタマイズ")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/652f936c75de3cc660641658a8af3f3a.png"
                        alt="ヒューマンロボットインタラクション"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/652f936c75de3cc660641658a8af3f3a.png", "ヒューマンロボットインタラクション")}
                      />
                    </div>
                  </>
                ) : item.id === '05' ? (
                  <>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/ba3a873463c44c650fe64438dfa3061a.png"
                        alt="ニットスキンウェアラブルデバイス"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/f98ff5317c9ebc76c5fef8b6f59db8d8.png", "ニットスキンウェアラブルデバイス")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/884edbb559bd6e795a22f960a09ebd19.png"
                        alt="医療機器ニットカバー"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/c94a552d9b6e84335448175234517214.png", "医療機器ニットカバー")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/a5c938ba13d4ea2ce9eb094b83328fe4.png"
                        alt="バイオセンサー内蔵ニット"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/447e90bb3fb4009dc132b1eee495f74f.png", "バイオセンサー内蔵ニット")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/eec9c6bb2c329aa367179e2428cdfa54.png"
                        alt="抗菌医療用ニット素材"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/f5f0b110528cf00666dc519fcd08d539.png", "抗菌医療用ニット素材")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/8e3fb29860af4d3827abb08c6aa8d255.png"
                        alt="衛生的な医療用テキスタイル"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/8e3fb29860af4d3827abb08c6aa8d255.png", "衛生的な医療用テキスタイル")}
                      />
                    </div>
                  </>
                ) : item.id === '06' ? (
                  <>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://public.readdy.ai/ai/img_res/1d729a11c78a62e953afe2c69ca57c52.jpg"
                        alt="3Dニッティングサポーター"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://readdy.ai/api/search-image?query=Custom-fitted%20medical%20support%20brace%20made%20with%203D%20knitting%20technology%2C%20comfortable%20orthopedic%20knee%20support%20with%20advanced%20textile%20materials%2C%20patient%20wearing%20soft%20knitted%20medical%20brace%2C%20modern%20rehabilitation%20equipment%20with%20integrated%20smart%20textile%20features&width=800&height=600&seq=health-slide-01-large&orientation=landscape", "3Dニッティングサポーター")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Medical%20back%20support%20corset%20with%20graduated%20compression%20knitting%2C%20therapeutic%20lumbar%20support%20with%20soft%20textile%20covering%2C%20patient%20comfort%20with%20knitted%20medical%20device%2C%20advanced%20orthopedic%20textile%20technology&width=300&height=200&seq=health-slide-02&orientation=landscape"
                        alt="段階圧迫コルセット"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://readdy.ai/api/search-image?query=Medical%20back%20support%20corset%20with%20graduated%20compression%20knitting%2C%20therapeutic%20lumbar%20support%20with%20soft%20textile%20covering%2C%20patient%20comfort%20with%20knitted%20medical%20device%2C%20advanced%20orthopedic%20textile%20technology&width=800&height=600&seq=health-slide-02-large&orientation=landscape", "段階圧迫コルセット")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/872838a88a64ff2ff9af6c1cce87f857.png"
                        alt="高機能医療用ニット"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/f4994205aeecc5123b0de926b52bd325.png", "高機能医療用ニット")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/f4256506142417393d3d4f58466aba6e.png"
                        alt="センサー内蔵リハビリ用具"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/673adc99a05051c9a40d389f353a28be.png", "センサー内蔵リハビリ用具")}
                      />
                    </div>
                    <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                      <img 
                        src="https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/d6e6ebd7f0b15ce9e999482a09d05526.png"
                        alt="衛生的な医療用テキスタイル"
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                        group-hover:brightness-110"
                        onClick={() => handleImageClick("https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/d6e6ebd7f0b15ce9e999482a09d05526.png", "衛生的な医療用テキスタイル")}
                      />
                    </div>
                  </>
                ) : (
                  <div className="aspect-video rounded-lg overflow-hidden cursor-pointer group">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-5
                      group-hover:brightness-110"
                      onClick={() => handleImageClick(item.image, item.title)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Project details */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-stone-800 mb-4">{t('portfolio.modal.projectOverview')}</h3>
                <p className="text-stone-600 leading-relaxed mb-6">
                  {item.description}
                </p>
                
                <h4 className="text-lg font-semibold text-stone-800 mb-3">{t('portfolio.modal.mainFeatures')}</h4>
                <ul className="space-y-2">
                  {item.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-check-line text-amber-600 mt-1 mr-2"></i>
                      <span className="text-stone-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-stone-800 mb-3">{t('portfolio.modal.technologies')}</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {item.technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-stone-100 text-stone-700 text-sm rounded-lg">
                      {tech}
                    </span>
                  ))}
                </div>
                
                <h4 className="text-lg font-semibold text-stone-800 mb-3">{t('portfolio.modal.results')}</h4>
                <ul className="space-y-2">
                  {item.results.map((result, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-star-line text-amber-600 mt-1 mr-2"></i>
                      <span className="text-stone-600">{result}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-stone-200">
              <button 
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer"
                onClick={onClose}
              >
                {t('portfolio.modal.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <ImageModal 
        imageUrl={selectedImage?.url || ''}
        imageAlt={selectedImage?.alt || ''}
        isOpen={!!selectedImage}
        onClose={handleImageModalClose}
      />
    </>
  );
}

// PortfolioSection component with i18n
export default function PortfolioSection() {
  const { isVisible, setElementRef } = useScrollAnimation();
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const portfolioItems: PortfolioItem[] = [
    {
      id: '01',
      title: t('portfolio.item1.title'),
      subtitle: t('portfolio.item1.subtitle'),
      tags: [t('portfolio.item1.tag1'), t('portfolio.item1.tag2'), t('portfolio.item1.tag3')],
      image: 'https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/b5bfd448474a4b96380a3f4f0726780d.png',
      link: '#',
      description: t('portfolio.item1.description'),
      features: [
        t('portfolio.item1.feature1'),
        t('portfolio.item1.feature2'),
        t('portfolio.item1.feature3'),
        t('portfolio.item1.feature4'),
        t('portfolio.item1.feature5')
      ],
      technologies: ['インダストリアル・ニッティング', 'IoTセンサー', 'LED照明技術', 'プロジェクションマッピング', 'スマートテキスタイル'],
      results: [
        t('portfolio.item1.result1'),
        t('portfolio.item1.result2'),
        t('portfolio.item1.result3'),
        t('portfolio.item1.result4'),
        t('portfolio.item1.result5')
      ]
    },
    {
      id: '02',
      title: t('portfolio.item2.title'),
      subtitle: t('portfolio.item2.subtitle'),
      tags: [t('portfolio.item2.tag1'), t('portfolio.item2.tag2'), t('portfolio.item2.tag3')],
      image: 'https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/52a13b2091d521bd416854d64cc46746.png',
      link: '#',
      description: t('portfolio.item2.description'),
      features: [
        t('portfolio.item2.feature1'),
        t('portfolio.item2.feature2'),
        t('portfolio.item2.feature3'),
        t('portfolio.item2.feature4'),
        t('portfolio.item2.feature5')
      ],
      technologies: ['インダストリアル・ニッティング', 'テキスタイルデザイン', '機能性繊維', '3Dニッティング', 'サステナブル素材'],
      results: [
        t('portfolio.item2.result1'),
        t('portfolio.item2.result2'),
        t('portfolio.item2.result3'),
        t('portfolio.item2.result4'),
        t('portfolio.item2.result5')
      ]
    },
    {
      id: '03',
      title: t('portfolio.item3.title'),
      subtitle: t('portfolio.item3.subtitle'),
      tags: [t('portfolio.item3.tag1'), t('portfolio.item3.tag2'), t('portfolio.item3.tag3')],
      image: 'https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/7eacae16afea68ea3f68001d69284242.png',
      link: '#',
      description: t('portfolio.item3.description'),
      features: [
        t('portfolio.item3.feature1'),
        t('portfolio.item3.feature2'),
        t('portfolio.item3.feature3'),
        t('portfolio.item3.feature4'),
        t('portfolio.item3.feature5')
      ],
      technologies: ['インダストリアル・ニッティング', 'テキスタイルデザイン', '機能性繊維', 'カスタムフィット技術', 'サステナブル素材'],
      results: [
        t('portfolio.item3.result1'),
        t('portfolio.item3.result2'),
        t('portfolio.item3.result3'),
        t('portfolio.item3.result4'),
        t('portfolio.item3.result5')
      ]
    },
    {
      id: '04',
      title: t('portfolio.item4.title'),
      subtitle: t('portfolio.item4.subtitle'),
      tags: [t('portfolio.item4.tag1'), t('portfolio.item4.tag2'), t('portfolio.item4.tag3')],
      image: 'https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/8b4092d01bf48119b7bb7cf5494417e9.png',
      link: '#',
      description: t('portfolio.item4.description'),
      features: [
        t('portfolio.item4.feature1'),
        t('portfolio.item4.feature2'),
        t('portfolio.item4.feature3'),
        t('portfolio.item4.feature4'),
        t('portfolio.item4.feature5')
      ],
      technologies: ['インダストリアル・ニッティング', 'ロボティクス', '機能性繊維', 'ヒューマンインターフェース', 'スマートテキスタイル'],
      results: [
        t('portfolio.item4.result1'),
        t('portfolio.item4.result2'),
        t('portfolio.item4.result3'),
        t('portfolio.item4.result4'),
        t('portfolio.item4.result5')
      ]
    },
    {
      id: '05',
      title: t('portfolio.item5.title'),
      subtitle: t('portfolio.item5.subtitle'),
      tags: [t('portfolio.item5.tag1'), t('portfolio.item5.tag2'), t('portfolio.item5.tag3')],
      image: 'https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/e257d817cab1d9b07116a09841b4da36.png',
      link: '#',
      description: t('portfolio.item5.description'),
      features: [
        t('portfolio.item5.feature1'),
        t('portfolio.item5.feature2'),
        t('portfolio.item5.feature3'),
        t('portfolio.item5.feature4'),
        t('portfolio.item5.feature5')
      ],
      technologies: ['インダストリアル・ニッティング', 'バイオセンサー', '抗菌繊維', 'スマートテキスタイル', 'メディカルデザイン'],
      results: [
        t('portfolio.item5.result1'),
        t('portfolio.item5.result2'),
        t('portfolio.item5.result3'),
        t('portfolio.item5.result4'),
        t('portfolio.item5.result5')
      ]
    },
    {
      id: '06',
      title: t('portfolio.item6.title'),
      subtitle: t('portfolio.item6.subtitle'),
      tags: [t('portfolio.item6.tag1'), t('portfolio.item6.tag2'), t('portfolio.item6.tag3')],
      image: 'https://static.readdy.ai/image/c38ca4b43662e8df5be3570b092391f1/1bb03953d4736a90983dd522be6a8979.png',
      link: '#',
      description: t('portfolio.item6.description'),
      features: [
        t('portfolio.item6.feature1'),
        t('portfolio.item6.feature2'),
        t('portfolio.item6.feature3'),
        t('portfolio.item6.feature4'),
        t('portfolio.item6.feature5')
      ],
      technologies: ['3Dニッティング', 'メディカルテキスタイル', '圧迫療法技術', 'バイオメカニクス', '機能性繊維'],
      results: [
        t('portfolio.item6.result1'),
        t('portfolio.item6.result2'),
        t('portfolio.item6.result3'),
        t('portfolio.item6.result4'),
        t('portfolio.item6.result5')
      ]
    }
  ];

  const handleItemClick = (item: PortfolioItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-stone-50 to-amber-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            ref={setElementRef}
            className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-thin text-gray-900 mb-6">{t('portfolio.title')}</h2>
              <p className="text-base md:text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">{t('portfolio.description')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {portfolioItems.map((item, index) => (
                <div 
                  key={item.id}
                  className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                  onClick={() => handleItemClick(item)}
                >
                  {/* 横長画像 */}
                  <div className="relative overflow-hidden aspect-[16/9]">
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold text-stone-600 z-10">
                      {item.id}
                    </div>
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  {/* テキストコンテンツ */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-stone-800 mb-2 group-hover:text-amber-600 transition-colors duration-300">{item.title}</h3>
                    <p className="text-stone-600 text-sm leading-relaxed mb-4">{item.subtitle}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="px-3 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">{tag}</span>
                      ))}
                    </div>
                    
                    <div className="flex items-center text-amber-600 text-sm font-medium group-hover:text-amber-700 transition-colors duration-300">
                      {t('aboutViewDetails')}
                      <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <PortfolioModal 
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
}
